import React, { useEffect, useRef, useState, memo } from "react";
import ApexCharts from "apexcharts";
import { useSelector } from "react-redux";

const PieChart = ({
  icon,
  title,
  series,
  labels,
  values,
  dataLabelsOutside = false,
  colors = [
    "#6AA4F7", // Mobile Fuel Combustion (teal)
    "#52ADC1", // Stationary Fuel Consumption (blue)
    "#EAA89D", // Fugitive Emissions (light blue)
    "#458276", // Purchased Electricity (light green)
    "#E8C458", // Purchased Heat & Steam (green)
    "#FF9F43", // Orange fallback
    "#7367F0", // Purple fallback
    "#28C76F", // Green fallback
    "#EA5455", // Red fallback
  ],
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const isPermissionCompleted = user?.isCompleted;

  // viewBox for overlay svg (kept fixed, we scale px -> this unit space)
  const VB_W = 330;
  const VB_H = 250;

  // dynamic geometry of the actual rendered pie (in viewBox units)
  const [geo, setGeo] = useState({ cx: VB_W / 2, cy: VB_H / 2, radius: 78 });

  const realTotal = series.reduce((a, b) => a + b, 0);
  // If realTotal is 0, we fake the series so ApexCharts and leader lines can draw equal slices
  const apexSeries =
    realTotal === 0 && series.length > 0 ? series.map(() => 1) : series;
  const displayTotal = realTotal === 0 ? series.length : realTotal;

  const displayLabels = labels;
  const displayColors = colors;

  // Measure the actual pie's center + radius from the DOM and convert
  // those pixel values into our fixed viewBox coordinate space.
  const measureChartGeometry = () => {
    if (!chartRef.current) return;

    const containerRect = chartRef.current.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height) return;

    // ApexCharts renders each pie slice as path.apexcharts-pie-area
    const paths = chartRef.current.querySelectorAll("path.apexcharts-pie-area");
    if (!paths.length) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    paths.forEach((p) => {
      const r = p.getBoundingClientRect();
      minX = Math.min(minX, r.left);
      minY = Math.min(minY, r.top);
      maxX = Math.max(maxX, r.right);
      maxY = Math.max(maxY, r.bottom);
    });

    if (!isFinite(minX)) return;

    const cxPx = (minX + maxX) / 2 - containerRect.left;
    const cyPx = (minY + maxY) / 2 - containerRect.top;
    const radiusPx = (maxX - minX) / 2;

    const scaleX = VB_W / containerRect.width;
    const scaleY = VB_H / containerRect.height;
    const scale = (scaleX + scaleY) / 2;

    setGeo({
      cx: cxPx * scaleX,
      cy: cyPx * scaleY,
      radius: radiusPx * scale,
    });
  };

  const renderLeaderLines = () => {
    if (!dataLabelsOutside || series.length === 0) return null;

    const { cx, cy, radius } = geo;
    let currentAngle = -90;

    // ✅ INCREASED: Push labels WAY outside the pie (was 65, then 115, now 145+)
    const LABEL_DISTANCE = 145;

    const items = apexSeries.map((val, index) => {
      const sliceAngle = displayTotal > 0 ? (val / displayTotal) * 360 : 0;
      const midAngle = currentAngle + sliceAngle / 2;
      currentAngle += sliceAngle;

      const rad = (midAngle * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      // Start exactly on the slice edge
      const x1 = cx + radius * cos;
      const y1 = cy + radius * sin;

      // ✅ STRAIGHT LINE: Direct path from slice to label (way outside now)
      const x2 = cx + LABEL_DISTANCE * cos;
      const y2 = cy + LABEL_DISTANCE * sin;

      const isRight = cos >= 0;

      // Actual value for percentage calculation
      const actualVal = series[index];
      const percentage =
        realTotal > 0 ? ((actualVal / realTotal) * 100).toFixed(1) : 0;

      return {
        index,
        val: percentage,
        color: displayColors[index],
        x1,
        y1,
        x2,
        y2,
        isRight,
        midAngle,
      };
    });

    const rightItems = items
      .filter((it) => it.isRight)
      .sort((a, b) => a.y2 - b.y2);
    const leftItems = items
      .filter((it) => !it.isRight)
      .sort((a, b) => a.y2 - b.y2);

    // ✅ Simplified collision detection for straight lines
    const applyAntiCollision = (groupList) => {
      const minGap = 22; // Space between labels

      // Push down overlapping items
      for (let i = 1; i < groupList.length; i++) {
        if (groupList[i].y2 < groupList[i - 1].y2 + minGap) {
          groupList[i].y2 = groupList[i - 1].y2 + minGap;
        }
      }

      // Constrain within bounds (increased to 250 for longer lines)
      if (groupList.length > 0 && groupList[groupList.length - 1].y2 > 250) {
        groupList[groupList.length - 1].y2 = 250;
        for (let i = groupList.length - 2; i >= 0; i--) {
          if (groupList[i].y2 > groupList[i + 1].y2 - minGap) {
            groupList[i].y2 = groupList[i + 1].y2 - minGap;
          }
        }
      }

      if (groupList.length > 0 && groupList[0].y2 < 0) {
        groupList[0].y2 = 0;
        for (let i = 1; i < groupList.length; i++) {
          if (groupList[i].y2 < groupList[i - 1].y2 + minGap) {
            groupList[i].y2 = groupList[i - 1].y2 + minGap;
          }
        }
      }
    };

    applyAntiCollision(rightItems);
    applyAntiCollision(leftItems);

    return (
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible" }}
      >
        {items.map((item) => {
          const { index, val, color, x1, y1, x2, y2, isRight } = item;

          return (
            <g key={index}>
              {/* Dot at slice edge */}
              <circle cx={x1} cy={y1} r={2.5} fill={color} />

              {/* ✅ VISIBLE LINE: Thicker, more prominent (was 1.5, now 2) */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.8"
              />

              {/* ✅ Label positioned further away with better spacing */}
              <text
                x={isRight ? x2 + 8 : x2 - 8}
                y={y2 + 3}
                textAnchor={isRight ? "start" : "end"}
                fill="#374151"
                fontSize="12"
                fontWeight="700"
                fontFamily="sans-serif"
              >
                {val}%
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const options = {
    chart: {
      type: "pie",
      height: 250,
      width: "100%",
      animations: {
        enabled: false,
      },
    },
    labels: displayLabels, // ✅ Use filtered labels
    colors: displayColors, // ✅ Use filtered colors
    dataLabels: {
      enabled: !dataLabelsOutside,
      style: { colors: ["#000"], fontWeight: 400 },
      formatter: (val, opts) => {
        const actualVal = series[opts.seriesIndex] || 0;
        const percentage =
          realTotal > 0 ? ((actualVal / realTotal) * 100).toFixed(1) : 0;
        return `${percentage}%`;
      },
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          if (values && values[opts.seriesIndex] !== undefined) {
            return values[opts.seriesIndex];
          }
          return series[opts.seriesIndex] || 0;
        },
      },
    },
    legend: { show: false },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 220,
          },
        },
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      const chart = new ApexCharts(chartRef.current, {
        ...options,
        series: apexSeries,
      });
      chartInstanceRef.current = chart;
      chart.render().then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(measureChartGeometry);
        });
      });
      return () => chart.destroy();
    }
  }, [series, labels]);

  // Recalculate geometry on window resize
  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(measureChartGeometry);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="w-full bg-white border border-[#3DA3BA59] rounded-2xl p-4 flex flex-col gap-4">
      {/* Title - takes full width */}
      <div className="flex items-center gap-2">
        <span className="text-[#06513D] text-2xl md:text-3xl flex-shrink-0">
          {icon}
        </span>
        <h2 className="text-base md:text-lg font-semibold text-[#06513D]">
          {title}
        </h2>
      </div>

      {/* Chart and Legends */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10 flex-wrap w-full">
        {/* Left: Chart */}
        <div className="flex-1 w-full md:w-1/2 min-w-0">
          <div
            className={`relative w-full max-w-[330px] mx-auto overflow-visible ${
              !isPermissionCompleted
                ? "blur-sm opacity-60 pointer-events-none"
                : ""
            }`}
          >
            <div ref={chartRef} className="w-full" />
            {renderLeaderLines()}
          </div>
        </div>

        {/* Right: Legends - Show ALL from response */}
        <div
          className={`flex-1 w-full md:w-1/2 pl-0 md:pl-4 min-w-0 ${!isPermissionCompleted ? "blur-sm opacity-60 pointer-events-none" : ""}`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
            {labels.map((label, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="w-7 h-7 mr-2 rounded-sm"
                  style={{ backgroundColor: colors[index] }}
                ></div>
                <span className="text-xs sm:text-sm text-gray-700 capitalize">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PieChart);
